import React, { useEffect } from 'react';
import { Button, Typography, Box, Container, createTheme, ThemeProvider, CssBaseline, Paper } from '@mui/material';
import { useNavigate } from 'react-router-dom';
import { styled, keyframes } from '@mui/system';
import CheckCircleOutlineIcon from '@mui/icons-material/CheckCircleOutline';
import ReceiptLongOutlinedIcon from '@mui/icons-material/ReceiptLongOutlined';

const defaultTheme = createTheme({
  palette: {
    primary: { main: '#e88ba9' },
    background: { default: '#fffafb' },
    text: { primary: '#5c434a', secondary: '#8c7077' },
  },
  typography: {
    fontFamily: '"Outfit", "Inter", sans-serif',
    h2: { fontFamily: '"Playfair Display", serif', color: '#d46a89', fontWeight: 800 },
    h3: { fontFamily: '"Playfair Display", serif', color: '#d46a89', fontWeight: 800 },
    h5: { fontFamily: '"Playfair Display", serif', color: '#d46a89', fontWeight: 600 },
  },
});

const popAnimation = keyframes`
  0% { transform: scale(0.5); opacity: 0; }
  70% { transform: scale(1.1); opacity: 1; }
  100% { transform: scale(1); opacity: 1; }
`;

const floatAnimation = keyframes`
  0% { transform: translateY(0px); }
  50% { transform: translateY(-10px); }
  100% { transform: translateY(0px); }
`;

const SuccessCard = styled(Paper)({
  borderRadius: '32px',
  border: '1px solid #fcf4f4',
  boxShadow: '0 20px 60px rgba(232, 139, 169, 0.15)',
  padding: '60px 40px',
  background: '#fff',
  display: 'flex',
  flexDirection: 'column',
  alignItems: 'center',
  maxWidth: '600px',
  margin: '0 auto',
  animation: `${popAnimation} 0.6s cubic-bezier(0.175, 0.885, 0.32, 1.275) forwards`,
  position: 'relative',
  overflow: 'hidden'
});

const ActionButton = styled(Button)({
  background: '#e88ba9',
  borderRadius: '30px',
  color: 'white',
  padding: '14px 32px',
  boxShadow: '0 8px 16px rgba(232, 139, 169, 0.3)',
  fontWeight: 700,
  textTransform: 'none',
  fontSize: '1.1rem',
  transition: 'all 0.3s ease',
  '&:hover': { transform: 'translateY(-2px)', boxShadow: '0 12px 20px rgba(212, 106, 137, 0.5)', background: '#d46a89' },
});

const ConfirmPage = () => {
  const navigate = useNavigate();

  useEffect(() => {
    window.scrollTo(0, 0);
  }, []);

  return (
    <ThemeProvider theme={defaultTheme}>
      <CssBaseline />
      <Box
        sx={{
          minHeight: '100vh',
          pt: '140px',
          pb: 8,
          background: 'linear-gradient(135deg, #fffafb 0%, #ffedf4 100%)',
          display: 'flex',
          alignItems: 'center'
        }}
      >
        <Container maxWidth="md">
          <SuccessCard elevation={0}>
            {/* Decorative Background Elements */}
            <Box sx={{ position: 'absolute', top: -50, right: -50, width: 150, height: 150, borderRadius: '50%', background: 'rgba(232, 139, 169, 0.05)' }} />
            <Box sx={{ position: 'absolute', bottom: -30, left: -30, width: 100, height: 100, borderRadius: '50%', background: 'rgba(232, 139, 169, 0.08)' }} />

            <Box sx={{ animation: `${floatAnimation} 3s ease-in-out infinite`, mb: 3 }}>
              <CheckCircleOutlineIcon sx={{ fontSize: 100, color: '#e88ba9' }} />
            </Box>

            <Typography variant="h2" sx={{ mb: 2, textAlign: 'center', fontSize: { xs: '2.5rem', md: '3.5rem' } }}>
              Order Confirmed!
            </Typography>

            <Typography sx={{ color: '#8c7077', fontSize: '1.2rem', textAlign: 'center', mb: 4, maxWidth: '400px', lineHeight: 1.6 }}>
              Thank you for satisfying your sweet tooth with us. Your delicious treats will be prepared with love.
            </Typography>

            <Box sx={{ p: 3, bgcolor: '#fcf4f4', borderRadius: '20px', mb: 5, width: '100%', border: '1px solid rgba(232, 139, 169, 0.2)' }}>
              <Typography sx={{ color: '#d46a89', fontWeight: 600, textAlign: 'center', fontSize: '1rem', display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 1 }}>
                <ReceiptLongOutlinedIcon fontSize="small" />
                Payment will be collected upon delivery/pickup.
              </Typography>
            </Box>

            <Box sx={{ display: 'flex', gap: 2, flexDirection: { xs: 'column', sm: 'row' }, width: '100%', justifyContent: 'center' }}>
              <Button
                variant="outlined"
                sx={{
                  borderRadius: '30px', borderColor: '#e88ba9', color: '#e88ba9', padding: '12px 24px', fontWeight: 600,
                  '&:hover': { borderColor: '#d46a89', bgcolor: '#fcf4f4' }
                }}
                onClick={() => navigate('/menu')}
              >
                Continue Shopping
              </Button>
              <ActionButton onClick={() => navigate('/profile')}>
                View Order Details
              </ActionButton>
            </Box>
          </SuccessCard>
        </Container>
      </Box>
    </ThemeProvider>
  );
};

export default ConfirmPage;